import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Heart, ArrowLeft, Check, Sparkles, FileDown, Loader2, Menu, X, LogOut, CreditCard, ShieldCheck, AlertTriangle, CalendarCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SECTIONS } from "@/data/sections";
import AIAssistant from "@/components/AIAssistant";
import Vault from "@/components/Vault";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/data/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "evergrace_planner_id";

export default function Planner() {
  const navigate = useNavigate();
  const { user, loading: authLoading, logout, refresh } = useAuth();
  const [plannerId, setPlannerId] = useState(null);
  const [data, setData] = useState({});
  const [active, setActive] = useState("welcome");
  const [saving, setSaving] = useState(false);
  const [ready, setReady] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [billingBusy, setBillingBusy] = useState(false);
  const [resumeBusy, setResumeBusy] = useState(false);
  const [ai, setAi] = useState({ open: false, mode: "general", context: "", fieldLabel: "", sectionId: "", fieldKey: "" });
  const saveTimer = useRef(null);

  // Init planner (auth + paywall aware)
  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/register"); return; }
    if (!user.is_paid) { navigate("/pricing"); return; }
    (async () => {
      try {
        const { data: p } = await axios.get(`${API}/planner/mine`);
        setPlannerId(p.planner_id);
        setData(p.data || {});
        localStorage.setItem(STORAGE_KEY, p.planner_id);
      } catch {
        toast.error("Could not load your records. Please refresh.");
      } finally {
        setReady(true);
      }
    })();
  }, [user, authLoading, navigate]);

  const persist = useCallback(async (nextData, id) => {
    if (!id) return;
    setSaving(true);
    try {
      await axios.put(`${API}/planner/${id}`, { data: nextData });
      toast.success("Progress saved", { duration: 1200 });
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  }, []);

  const handleChange = (sectionId, key, value) => {
    setData((prev) => {
      const next = { ...prev, [sectionId]: { ...(prev[sectionId] || {}), [key]: value } };
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => persist(next, plannerId), 900);
      return next;
    });
  };

  const manageBilling = async () => {
    setBillingBusy(true);
    try {
      const { data } = await axios.post(`${API}/payments/portal`, { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch {
      toast.error("Could not open billing. Please try again.");
      setBillingBusy(false);
    }
  };

  const resumeSubscription = async () => {
    setResumeBusy(true);
    try {
      await axios.post(`${API}/payments/resume`);
      await refresh();
      toast.success("Welcome back — your plan will continue.");
    } catch {
      toast.error("Could not resume your plan. Please try again.");
    } finally {
      setResumeBusy(false);
    }
  };

  const exportPdf = async () => {    if (!plannerId) return;
    setExporting(true);
    try {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      await axios.put(`${API}/planner/${plannerId}`, { data });
      const res = await axios.get(`${API}/planner/${plannerId}/export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "Peace-of-Mind-Legacy.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Your PDF is ready");
    } catch {
      toast.error("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  // Progress
  const totalFields = SECTIONS.reduce((n, s) => n + s.fields.length, 0);
  const filledFields = SECTIONS.reduce(
    (n, s) => n + s.fields.filter((f) => (data[s.id]?.[f.key] ?? "").toString().trim() !== "").length, 0);
  const progress = Math.round((filledFields / totalFields) * 100);

  const sectionProgress = (s) => {
    const filled = s.fields.filter((f) => (data[s.id]?.[f.key] ?? "").toString().trim() !== "").length;
    return { filled, total: s.fields.length, done: filled === s.fields.length };
  };

  const activeSection = SECTIONS.find((s) => s.id === active);

  const openAssist = (field) => {
    setAi({
      open: true,
      mode: field.aiMode || "general",
      context: data[active]?.[field.key] || "",
      fieldLabel: field.label,
      sectionId: active,
      fieldKey: field.key,
    });
  };

  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ivory">
        <Loader2 className="w-6 h-6 animate-spin text-sage" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ivory flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static z-40 inset-y-0 left-0 w-80 bg-navy text-ivory flex flex-col transition-transform ${
        sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      }`} data-testid="planner-sidebar">
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 group" data-testid="back-home-btn">
            <img src={LOGO_URL} alt="The Ever Grace Group" className="h-10 w-10 rounded-full object-cover" />
            <span className="font-serif text-xl"><span className="text-ivory">Ever</span> <span className="text-gold">Grace</span></span>
          </button>
          <button className="lg:hidden" onClick={() => setSidebarOpen(false)}><X className="w-5 h-5" /></button>
        </div>

        <div className="px-6 py-5 border-b border-white/10">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-ivory/70">Completion</span>
            <span className="text-gold font-semibold" data-testid="progress-percent">{progress}%</span>
          </div>
          <Progress value={progress} className="h-2 bg-white/15" data-testid="progress-bar" />
        </div>

        <nav className="flex-1 overflow-y-auto planner-scroll py-4">
          {SECTIONS.map((s, i) => {
            const sp = sectionProgress(s);
            const isActive = s.id === active;
            return (
              <button key={s.id} onClick={() => { setActive(s.id); setSidebarOpen(false); }}
                data-testid={`nav-${s.id}`}
                className={`w-full flex items-center gap-3 px-6 py-3 text-left transition-colors ${
                  isActive ? "bg-white/10 border-l-2 border-gold" : "border-l-2 border-transparent hover:bg-white/5"
                }`}>
                <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                  isActive ? "bg-gold text-navy" : "bg-white/10 text-ivory"
                }`}>
                  <s.icon className="w-4 h-4" strokeWidth={1.5} />
                </span>
                <span className="flex-1">
                  <span className="block font-serif text-lg leading-tight">{s.title}</span>
                  <span className="block text-xs text-ivory/50">{sp.filled}/{sp.total} completed</span>
                </span>
                {sp.done && <Check className="w-4 h-4 text-gold" strokeWidth={2.5} />}
              </button>
            );
          })}
          <button onClick={() => { setActive("vault"); setSidebarOpen(false); }} data-testid="nav-vault"
            className={`w-full flex items-center gap-3 px-6 py-3 text-left transition-colors mt-2 border-t border-white/10 ${
              active === "vault" ? "bg-white/10 border-l-2 border-gold" : "border-l-2 border-transparent hover:bg-white/5"
            }`}>
            <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
              active === "vault" ? "bg-gold text-navy" : "bg-white/10 text-ivory"
            }`}>
              <ShieldCheck className="w-4 h-4" strokeWidth={1.5} />
            </span>
            <span className="flex-1">
              <span className="block font-serif text-lg leading-tight">Digital Vault</span>
              <span className="block text-xs text-ivory/50">Documents &amp; secure notes</span>
            </span>
          </button>
        </nav>

        <div className="p-4 border-t border-white/10">
          <Button onClick={exportPdf} disabled={exporting} data-testid="export-pdf-btn"
            className="w-full bg-gold hover:bg-gold/90 text-navy rounded-full font-semibold">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileDown className="w-4 h-4 mr-2" />}
            Export to PDF
          </Button>
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <main className="flex-1 min-w-0">
        <header className="sticky top-0 z-20 backdrop-blur-md bg-ivory/85 border-b border-gold/20 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setSidebarOpen(true)} data-testid="menu-btn"><Menu className="w-5 h-5 text-navy" /></button>
            <button onClick={() => navigate("/")} className="hidden sm:flex items-center gap-1 text-sm text-navy/70 hover:text-navy transition-colors">
              <ArrowLeft className="w-4 h-4" /> Home
            </button>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/book" data-testid="planner-book-link"
              className="hidden sm:flex items-center gap-1 text-sm text-navy hover:text-gold transition-colors">
              <CalendarCheck className="w-4 h-4" /> Book
            </Link>
            <span className="text-sm text-sage flex items-center gap-1" data-testid="save-status">
              {saving ? <><Loader2 className="w-3 h-3 animate-spin" /> Saving…</> : <><Check className="w-3 h-3" /> All changes saved</>}
            </span>
            <Button variant="outline" onClick={() => setAi({ open: true, mode: "general", context: "", fieldLabel: "", sectionId: active, fieldKey: "" })}
              data-testid="open-ai-btn"
              className="rounded-full border-sage text-navy hover:bg-sage/10">
              <Sparkles className="w-4 h-4 mr-2 text-sage" /> AI Assistant
            </Button>
            {user ? (
              <div className="flex items-center gap-1" data-testid="account-area">
                {user.has_billing && (
                  <Button variant="ghost" onClick={manageBilling} disabled={billingBusy}
                    data-testid="manage-billing-btn" title="Manage billing"
                    className="text-navy hover:bg-navy/5 rounded-full gap-2">
                    {billingBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                    <span className="hidden sm:inline text-sm">Billing</span>
                  </Button>
                )}
                <span className="hidden sm:inline text-sm font-serif text-navy px-1" data-testid="account-name">{user.name || user.email}</span>
                <Button variant="ghost" size="icon" onClick={async () => { await logout(); navigate("/"); }}
                  data-testid="logout-btn" className="text-navy hover:bg-navy/5 rounded-full">
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            ) : null}
          </div>
        </header>

        {user?.cancel_at_period_end && (
          <div className="bg-gold/15 border-b border-gold/40 px-6 py-3 flex flex-col sm:flex-row items-center justify-center gap-3 text-center" data-testid="grace-notice-banner">
            <span className="text-sm text-navy">
              Your plan is set to end
              {user.cancel_at ? ` on ${new Date(user.cancel_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}` : " soon"}.
              You'll keep full access until then.
            </span>
            <Button onClick={resumeSubscription} disabled={resumeBusy} data-testid="resume-plan-btn"
              className="bg-navy hover:bg-navy/90 text-ivory rounded-full h-8 px-4 text-sm">
              {resumeBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
              Keep my plan
            </Button>
          </div>
        )}

        {user?.payment_failed && (
          <div className="bg-destructive/10 border-b border-destructive/30 px-6 py-3 flex flex-col sm:flex-row items-center justify-center gap-3 text-center" data-testid="payment-failed-banner">
            <span className="text-sm text-destructive flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              A recent payment didn't go through. Please update your payment method to keep your access.
            </span>
            {user.has_billing && (
              <Button onClick={manageBilling} disabled={billingBusy} data-testid="fix-payment-btn"
                className="bg-destructive hover:bg-destructive/90 text-white rounded-full h-8 px-4 text-sm">
                {billingBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
                Fix payment
              </Button>
            )}
          </div>
        )}

        <div className="max-w-3xl mx-auto px-6 py-12">
          {active === "vault" ? <Vault /> : (
          <>
          {/* Divider header */}
          <motion.div key={activeSection.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="mb-10 text-center border-b border-gold/25 pb-10">
            <span className="inline-flex w-14 h-14 rounded-full bg-sage/15 items-center justify-center mb-4">
              <activeSection.icon className="w-6 h-6 text-navy" strokeWidth={1.5} />
            </span>
            <p className="uppercase tracking-[0.3em] text-xs text-sage mb-2">
              Section {SECTIONS.findIndex((s) => s.id === active) + 1} of 9
            </p>
            <h1 className="font-serif text-4xl md:text-5xl text-navy mb-3" data-testid="section-title">{activeSection.title}</h1>
            <p className="text-charcoal/70 max-w-lg mx-auto">{activeSection.intro}</p>
          </motion.div>

          {/* Worksheet */}
          <div className="space-y-7">
            {activeSection.fields.map((field) => {
              const value = data[active]?.[field.key] ?? "";
              return (
                <div key={field.key} className="fade-up">
                  <div className="flex items-center justify-between mb-2">
                    <Label htmlFor={field.key} className="text-navy font-semibold text-sm">{field.label}</Label>
                    {field.assist && (
                      <button onClick={() => openAssist(field)} data-testid={`assist-${field.key}`}
                        className="flex items-center gap-1 text-xs text-sage hover:text-gold transition-colors">
                        <Sparkles className="w-3.5 h-3.5" /> Help me write
                      </button>
                    )}
                  </div>

                  {field.type === "textarea" ? (
                    <Textarea id={field.key} value={value} placeholder={field.placeholder || ""}
                      onChange={(e) => handleChange(active, field.key, e.target.value)}
                      rows={4} data-testid={`field-${field.key}`}
                      className="bg-white border-gold/25 focus-visible:ring-sage focus-visible:border-sage resize-none" />
                  ) : field.type === "select" ? (
                    <Select value={value} onValueChange={(v) => handleChange(active, field.key, v)}>
                      <SelectTrigger data-testid={`field-${field.key}`} className="bg-white border-gold/25 focus:ring-sage">
                        <SelectValue placeholder="Select…" />
                      </SelectTrigger>
                      <SelectContent>
                        {field.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input id={field.key} type={field.type} value={value} placeholder={field.placeholder || ""}
                      onChange={(e) => handleChange(active, field.key, e.target.value)}
                      data-testid={`field-${field.key}`}
                      className="bg-white border-gold/25 focus-visible:ring-sage focus-visible:border-sage" />
                  )}
                </div>
              );
            })}
          </div>

          {/* Section nav */}
          <div className="flex justify-between mt-14 pt-8 border-t border-gold/25">
            {(() => {
              const idx = SECTIONS.findIndex((s) => s.id === active);
              return (
                <>
                  <Button variant="ghost" disabled={idx === 0}
                    onClick={() => setActive(SECTIONS[idx - 1].id)} data-testid="prev-section-btn"
                    className="text-navy hover:bg-navy/5">
                    <ArrowLeft className="w-4 h-4 mr-2" /> {idx > 0 ? SECTIONS[idx - 1].title : "Previous"}
                  </Button>
                  <Button disabled={idx === SECTIONS.length - 1}
                    onClick={() => { setActive(SECTIONS[idx + 1].id); window.scrollTo({ top: 0 }); }}
                    data-testid="next-section-btn"
                    className="bg-navy hover:bg-navy/90 text-ivory">
                    {idx < SECTIONS.length - 1 ? SECTIONS[idx + 1].title : "Finish"}
                  </Button>
                </>
              );
            })()}
          </div>
          </>
          )}
        </div>
      </main>

      <AIAssistant
        open={ai.open}
        onOpenChange={(v) => setAi((p) => ({ ...p, open: v }))}
        mode={ai.mode}
        sessionId={plannerId || "guest"}
        context={ai.context}
        fieldLabel={ai.fieldLabel}
        onInsert={ai.fieldKey ? (text) => {
          const existing = data[ai.sectionId]?.[ai.fieldKey] || "";
          handleChange(ai.sectionId, ai.fieldKey, existing ? existing + "\n\n" + text : text);
        } : undefined}
      />
    </div>
  );
}
